import { Controller } from '@hotwired/stimulus'

interface TurboFrameElement extends HTMLElement {
  src: string | null
  reload(): void
}

interface JsI18n {
  exists(key: string): boolean
  t(key: string): string
}

const MESSAGE_KEY = 'js.results.frame_error.message'
const MESSAGE_FALLBACK = 'These results could not be loaded. Check your connection and try again.'
const RETRY_KEY = 'js.results.frame_error.retry'
const RETRY_FALLBACK = 'Try again'

// Polls a "warming" results frame with backoff until the precomputed content
// arrives. After maxAttempts it drops to a slow steady pace and KEEPS polling
// silently — the skeleton stays on screen (no "taking longer" message) and a
// warm slower than the backoff window (staging proved it) still fills the
// frame without a manual refresh. Counters live on the frame element's
// dataset: reloads replace only the frame's innerHTML, so they survive the
// swap.
export default class extends Controller<HTMLElement> {
  static values = {
    interval: { type: Number, default: 3000 },
    backoff: { type: Number, default: 1.4 },
    maxAttempts: { type: Number, default: 8 },
    slowInterval: { type: Number, default: 30000 },
    maxFailures: { type: Number, default: 8 },
  }

  static targets = ['pending']

  declare readonly intervalValue: number
  declare readonly backoffValue: number
  declare readonly maxAttemptsValue: number
  declare readonly slowIntervalValue: number
  declare readonly maxFailuresValue: number
  declare readonly pendingTarget: HTMLElement
  declare readonly hasPendingTarget: boolean

  private timer?: ReturnType<typeof setTimeout>

  connect(): void {
    const frame = this.frame()
    if (!frame || !frame.src) return

    // This instance exists only because a reload swapped in fresh content, so
    // the consecutive-failure streak starts over.
    frame.dataset.warmPollFailures = '0'
    this.schedule(frame)
  }

  disconnect(): void {
    if (this.timer) clearTimeout(this.timer)
  }

  // Re-arms after every tick: a successful reload replaces the frame content
  // (disconnect clears the pending timer, the fresh controller re-connects),
  // while a FAILED reload leaves this instance alive — without the re-arm one
  // dropped poll would freeze the skeleton for good. Because success always
  // swaps in a NEW controller instance, consecutive ticks on the SAME instance
  // mean consecutive failures; cap them and surface a retry instead.
  private schedule(frame: TurboFrameElement): void {
    if (Number(frame.dataset.warmPollFailures || 0) >= this.maxFailuresValue) {
      this.renderError()
      return
    }

    const attempt = Number(frame.dataset.warmPollAttempt || 0)
    const delay = attempt >= this.maxAttemptsValue
      ? this.slowIntervalValue
      : this.intervalValue * this.backoffValue ** attempt

    this.timer = setTimeout(() => this.reload(frame, attempt), delay)
  }

  private reload(frame: TurboFrameElement, attempt: number): void {
    frame.dataset.warmPollAttempt = String(attempt + 1)
    // Counted before the request: only a successful swap (a new instance)
    // clears it.
    frame.dataset.warmPollFailures = String(Number(frame.dataset.warmPollFailures || 0) + 1)
    frame.reload()
    this.schedule(frame)
  }

  retry(): void {
    const frame = this.frame()
    if (!frame) return

    this.errorElement()?.remove()
    if (this.hasPendingTarget) this.pendingTarget.hidden = false
    this.element.dataset.resultsPending = 'true'
    frame.dataset.warmPollAttempt = '0'
    frame.dataset.warmPollFailures = '0'
    frame.reload()
    this.schedule(frame)
  }

  // Replaces the eternal skeleton with a retry affordance and stops claiming
  // the frame is still warming, so the page-level banner can settle.
  private renderError(): void {
    if (this.errorElement()) return

    if (this.hasPendingTarget) this.pendingTarget.hidden = true
    delete this.element.dataset.resultsPending

    const box = document.createElement('div')
    box.className = 'results-processing-banner'
    box.setAttribute('role', 'alert')
    box.dataset.resultsFrameError = 'true'

    const message = document.createElement('p')
    message.textContent = translate(MESSAGE_KEY, MESSAGE_FALLBACK)

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'btn btn--blue-stroke btn--small'
    button.dataset.action = `${this.identifier}#retry`
    button.textContent = translate(RETRY_KEY, RETRY_FALLBACK)

    box.append(message, button)
    this.element.append(box)
  }

  private errorElement(): HTMLElement | null {
    return this.element.querySelector('[data-results-frame-error]')
  }

  private frame(): TurboFrameElement | null {
    return this.element.closest('turbo-frame') as TurboFrameElement | null
  }
}

// Client-side strings go through the i18n-js bundle, falling back to English
// for locales Phrase has not translated yet (same shape as chart_controller).
function translate(key: string, fallback: string): string {
  const i18n = (window as unknown as { I18n?: JsI18n }).I18n
  return i18n?.exists?.(key) ? i18n.t(key) : fallback
}
